import React, { useEffect, useState } from 'react';
import { AddressWrapped } from 'shared/components/address/AddressWrapped';
import styles from './PlantItemDetails.module.scss';
import {
  Box,
  Button,
  Col,
  FormInput,
  H3,
  H4,
  Row,
  SectionPage,
  StatusLabel,
  Modal,
  InPageAlert,
  ErrorSummary,
  ComponentLoader,
} from '@snsw/react-component-library';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ScrollToTop from 'shared/utils/scrollToTop';
import { ListView } from 'shared/components/listView/ListView';
import style from './PlantItemDetails.module.scss';
import { isEmpty, isResidentialAddress, isStreetAddressValid, validate, validateExt } from 'shared/utils/Validation';
import { formatAddress } from 'shared/utils/Helpers';
import { PagePath } from 'shared/constants/pagePath';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { saveApplication } from 'shared/redux/applicationSlice';
import { removePlantItem, storePlantItems } from 'plantItemNew/redux/actions';
import FieldError from 'shared/components/fieldError';
import _ from 'lodash';
import { PasswordlessContinueSaveExitButtons } from 'shared/components/formNavigationButtons/PasswordlessContinueSaveExitButtons';
import { PlantItemConstants } from 'plantItemNew/constants/plantItemConstants';

export const PlantItemList = (props) => {
  ScrollToTop();

  const navigate = useNavigate();
  const plantItems = useSelector((state) => state.plantItems);
  const dispatch = useDispatch();

  const [itemAddress, setItemAddress] = useState(_.cloneDeep(plantItems.location));
  const [isItemAddressValid, setIsItemAddressValid] = useState(false);
  const [isItemAddressLoading, setIsItemAddressLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const [showEditAddressConfirmation, setShowEditAddressConfirmation] = useState(false);
  const [reasonToRegister, setReasonToRegister] = useState(plantItems.reasonToRegister || '');

  const listViewDetailLabels = [
    { title: 'Serial number:', property: 'serialNumber' },
    { title: 'Manufacturer:', property: 'manufacturer' },
    { title: 'Specific location of the item:', property: 'specificLocation' },
    { title: 'Unique identifier:', property: 'uniqueIdentifier' },
  ];

  const [errors, setErrors] = useState({
    itemAddress: false,
    reasonToRegister: false,
    itemList: false,
  });

  const [errorSummary, setErrorSummary] = useState([]);

  const isItemCompleted = (items) => {
    if (!Array.isArray(items)) return false;
    else
      return (
        items.length > 0 &&
        items.reduce((p, c) => {
          return p && c?.itemDetails?.isCompleted && c?.designDetails?.isCompleted && c?.relatedDetails?.isCompleted;
        }, true)
      );
  };

  const validators = [
    {
      fieldName: 'itemAddress',
      fieldValue: itemAddress,
      isValid: () => isItemAddressValid,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'itemAddress',
      fieldValue: itemAddress,
      isValid: () => isResidentialAddress(itemAddress),
      errorMessage: () => 'Please enter a valid street address',
    },
    {
      fieldName: 'itemAddress',
      fieldValue: itemAddress,
      isValid: () => isStreetAddressValid(itemAddress, 40),
      errorMessage: () => 'The length of the street address cannot be more than 40 characters',
    },
    {
      fieldName: 'reasonToRegister',
      fieldValue: reasonToRegister,
      isValid: (v) =>
        !isEmpty(v) ||
        !isItemAddressValid ||
        itemAddress?.details?.Address?.State === 'NSW' ||
        itemAddress?.state?.text === 'NSW',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'itemList',
      fieldValue: plantItems?.items,
      isValid: isItemCompleted,
      errorMessage: () => 'Please complete plant item',
    },
  ];

  useEffect(() => {
    if (!isItemAddressLoading && isItemAddressValid) {
      if (itemAddress?.details?.Address?.State === 'NSW') {
        setReasonToRegister('');
      }
    }

    //validate(validators, setErrors, errorMessages, setErrorSummary);
  }, [itemAddress, isItemAddressLoading, isItemAddressValid]);

  const renderRowTitle = (index, item) => {
    let status =
      !item?.itemDetails?.isCompleted || !item?.designDetails?.isCompleted || !item?.relatedDetails?.isCompleted ? (
        <div className={style.detailsStatus}>
          <StatusLabel text="Incomplete" variant="warning" />
        </div>
      ) : null;

    return (
      <>
        {status}
        <div className={style.listViewTitle}>{`${(index + 1).toString().padStart(2, '0')} ${
          item?.itemDetails?.itemTypeText
        }`}</div>
      </>
    );
  };

  const renderRowDetails = (index, item) => {
    let details = listViewDetailLabels.reduce((result, element) => {
      //if property is empty value, do not display
      if (item?.itemDetails[element.property]) {
        result.push(
          <div key={element.property}>
            {element.title} <span className={styles.listViewDetailsValue}>{item?.itemDetails[element.property]}</span>
          </div>,
        );
      }
      return result;
    }, []);

    return details;
  };

  const onAddPlantItem = () => {
    dispatch(storePlantItems({ location: itemAddress, reasonToRegister }));
    navigate(`${PagePath.plantDetails}/new`);
  };

  const onEditPlantItem = (index, item) => {
    if (!item?.itemDetails?.isCompleted) {
      navigate(`${PagePath.plantDetails}/${index}`);
    } else if (!item?.designDetails?.isCompleted) {
      navigate(`${PagePath.associatedDesign}/${index}`);
    } else {
      navigate(`${PagePath.relatedItemDetails}/${index}`);
    }
  };

  const onRemovePlantItem = (index, item) => {
    dispatch(removePlantItem(index));
    dispatch(saveApplication(true));
  };

  const onEditItemAddress = () => {
    if (plantItems?.items?.length) {
      setShowEditAddressConfirmation(true);
    } else {
      onEditItemAdderssConfirmed();
    }
  };

  const onEditItemAdderssConfirmed = () => {
    setItemAddress(null);
    setIsItemAddressValid(false);
    setShowEditAddressConfirmation(false);
  };

  const storeAddressAndReason = (save = true) => {
    validate(validators, setErrors);
    dispatch(
      storePlantItems({
        isCompleted:
          !errors.itemAddress &&
          !errors.reasonToRegister &&
          (itemAddress?.enterManually || typeof itemAddress?.details !== 'undefined'),
        location: itemAddress,
        reasonToRegister,
      }),
    );
    if (save) dispatch(saveApplication(true));
  };

  const onContinueClick = () => {
    let isValid = validateExt(validators, setErrors, setErrorSummary);

    if (!isValid) {
      setShowErrors(true);
    } else {
      setShowErrors(false);
      storeAddressAndReason();
      navigate(PagePath.declaration);
    }
  };

  const onChangeAddres = (data) => {
    setItemAddress(data.address);
  };

  return (
    <>
      {showErrors && <ErrorSummary errors={errorSummary} />}
      <SectionPage>
        <Row>
          <Col span={9}>
            <H3>{PlantItemDetailsPage.locationAddressOfItemLabel}</H3>
            {!isItemAddressValid && (
              <>
                <div className={styles.labelMsg}>
                  For fixed/ installed plant provide the working location address of the item.
                  <br />
                  For mobile plant provide the location address where the plant is inspected and/or stored.
                </div>

                <div id="address">
                  <AddressWrapped
                    autoValidate
                    baseUrl={`${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_URL_PREFIX}`}
                    label=" "
                    helpMessage="Start typing to search"
                    showErrors={showErrors && errors.itemAddress}
                    onChange={onChangeAddres}
                    onLoading={setIsItemAddressLoading}
                    onValidationChange={setIsItemAddressValid}
                    onDetailsRetrieved={() => setShowErrors(false)}
                    value={itemAddress}
                  />
                </div>
              </>
            )}

            {!isItemAddressLoading && isItemAddressValid && (
              <>
                <InPageAlert variant="info" title="All items will be registered to the address listed below." />
                <Box>
                  <div className={styles.titleLine}>
                    <H4 className={styles.titleLeft}>{formatAddress(itemAddress)}</H4>
                    <div className={styles.titleRight}>
                      <Button variant={'link'} onClick={onEditItemAddress}>
                        Edit
                      </Button>
                    </div>
                  </div>
                </Box>
              </>
            )}

            {showErrors && errors?.itemAddress?.invalid && <FieldError message={errors?.itemAddress?.errorMessage} />}

            {!isItemAddressLoading &&
              isItemAddressValid &&
              itemAddress?.details?.Address?.State !== 'NSW' &&
              itemAddress?.state?.text !== 'NSW' && (
                <FormInput
                  id="reasonToRegister"
                  label={PlantItemDetailsPage.reasonToRegisterToNSWLabel}
                  errorMessage={errors?.reasonToRegister?.errorMessage}
                  hasError={showErrors && errors?.reasonToRegister?.invalid}
                  onChange={(e, { value }) => setReasonToRegister(value)}
                  value={reasonToRegister}
                />
              )}
          </Col>
        </Row>
        {isItemAddressLoading && (
          <Row>
            <Col span={12}>
              <ComponentLoader fullPage />
            </Col>
          </Row>
        )}
      </SectionPage>
      {showEditAddressConfirmation && (
        <Modal
          title="This action will change the working location of all Plant Items in this application."
          description="Please press 'Confirm' to continue"
          buttons={[
            { text: 'Confirm', onClick: onEditItemAdderssConfirmed },
            {
              text: 'Cancel',
              onClick: () => setShowEditAddressConfirmation(false),
            },
          ]}
        />
      )}

      {!isItemAddressLoading && isItemAddressValid && (
        <SectionPage>
          <Row className={style.marginTop2rem}>
            <Col span={9}>
              <H3>Plant item details</H3>
              <div id="itemList">
                <ListView
                  title="Please add your plant item for the location supplied"
                  buttonAddTitle="+ Add a plant item"
                  onAdd={onAddPlantItem}
                  items={plantItems?.items}
                  renderRowTitle={renderRowTitle}
                  renderRowDetails={renderRowDetails}
                  onEdit={onEditPlantItem}
                  onRemove={onRemovePlantItem}
                  confirmationTitle="This action will delete the selected item from the application."
                  confirmationDescription="Please press 'confirm' to continue."
                  confirmRemoveTitle="Confirm"
                  errorMessage={errors?.itemList?.errorMessage}
                  hasError={showErrors && errors?.itemList?.invalid}
                />
              </div>
            </Col>
          </Row>
        </SectionPage>
      )}

      <PasswordlessContinueSaveExitButtons
        onContinueClick={onContinueClick}
        onPreSaving={() => {
          storeAddressAndReason(false);
        }}
        onBackClick={() => {
          storeAddressAndReason(false);
          navigate(PagePath.holderDetails);
        }}
        licenceType={PlantItemConstants.licenceType}
      />
    </>
  );
};
